import React, { useState, useEffect, useContext } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import SendIcon from "@mui/icons-material/Send";
import Stack from "@mui/material/Stack";
import PostAddIcon from "@mui/icons-material/PostAdd";
import Grid from "@mui/material/Grid";
import AuthContext from "../../context/AuthContext";
import Select from "react-dropdown-select";
import { createTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ThreadType, TopicType, UserType } from "../../utils/types";
import { getCSRFToken } from "../../utils/helperFunctions";
import TextEditor from "./TextEditor";

const ThreadForm = () => {
  let { user } = useContext(AuthContext);
  const currentUser: UserType = user;

  const [alertShow, setAlertShow] = useState(false);
  const [open, setOpen] = useState(false);
  const [topics, setTopics] = useState<TopicType[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [thread, setThread] = useState<ThreadType>({
    subject: "",
    content: "",
    topic: { id: 0, label: "", creator: "", creator_id: 0 },
    creator: currentUser,
  });
  const [pollQuestion, setPollQuestion] = useState("");
  const [createPoll, setCreatePoll] = useState(false);
  const [pollOptions, setPollOptions] = useState([""]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddOption = () => {
    setPollOptions([...pollOptions, ""]);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  let handleThread = async (event) => {
    event.preventDefault();
    const response = await fetch(`/api/createThread/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(thread),
    });

    const data = await response.json();
    if (createPoll) {
      try {
        const pollResponse = await fetch(`/api/threads/${data.id}/poll/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: pollQuestion,
            is_anonymous: isAnonymous,
            options: pollOptions,
          }),
        });
      } catch (error) {
        console.error(error);
      }
    }

    if (files.length > 0) {
      const formData = new FormData();
      formData.append("thread_id", data.id.toString());
      files.forEach((file) => {
        formData.append("file", file);
      });
      try {
        const response = await fetch(`/api/threads/${data.id}/files`, {
          method: "POST",
          headers: {
            "X-CSRFToken": getCSRFToken(),
          },
          body: formData,
        });

        if (response.ok) {
          console.log("Files uploaded successfully");
        } else {
          console.error("File upload failed", response);
          const errorData = await response.json();
          alert(
            `Fehler beim Hochladen der Datei(en): ${errorData?.error || "Unbekannter Fehler"}`
          );
        }
      } catch (error) {
        console.error("Error during file upload:", error);
        alert("Unbekannter Fehler beim Hochladen der Datei(en).");
      }
    }

    navigate(`/threads/${data.id}`);
  };

  useEffect(() => {
    const fetchTopics = async () => {
      const response = await fetch("/api/topics/");
      const data = await response.json();
      setTopics(data);
      if (window.location.pathname.includes("topic")) {
        if (
          parseInt(window.location.pathname.split("/").pop()) > 0 &&
          data.length > 0
        ) {
          setThread({
            ...thread,
            topic: {
              id: parseInt(window.location.pathname.split("/").pop()),
              label: data.find(
                (topic) =>
                  topic.id ===
                  parseInt(window.location.pathname.split("/").pop())
              )?.label,
              creator: "",
              creator_id: 0,
            },
          });
        }
      }
    };
    fetchTopics();
  }, []);

  // useEffect(() => {
  //   let isMounted = true;
  //   const fetchDefaultTopic = async () => {
  //     if (window.location.pathname.includes("topic")) {
  //       if (
  //         parseInt(window.location.pathname.split("/").pop()) > 0 &&
  //         isMounted
  //       ) {
  //         (prevThread) =>
  //           setThread({
  //             ...prevThread,
  //             topic: parseInt(window.location.pathname.split("/").pop()),
  //           });
  //       }
  //     }
  //   };
  //   fetchDefaultTopic();
  //   return () => {
  //     isMounted = false;
  //   };
  // }, []);

  // new thread button theme
  const theme = createTheme({
    palette: {
      primary: {
        main: "#6a5acd",
      },
    },
  });

  return (
    <div>
      <Grid container justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={handleClickOpen}
          endIcon={<PostAddIcon />}
          style={{
            maxWidth: "180px",
            maxHeight: "32px",
            minWidth: "100px",
            minHeight: "32px",
          }}
        >
          {window.innerWidth > 700 ? "Forumeintrag" : ""}
        </Button>
      </Grid>

      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
        <form onSubmit={handleThread} id="NewThreadForm">
          <Box>
            {alertShow && (
              <Alert severity="error" onClose={() => setAlertShow(false)}>
                Bitte melde dich an um einen Beitrag zu erstellen.{" "}
                <Link href="/login">Hier anmelden.</Link>
              </Alert>
            )}
          </Box>

          <DialogTitle>Neuer Beitrag</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Wähle das Thema deines Beitrages aus:
            </DialogContentText>

            <Select
              placeholder="Thema wählen"
              required
              onChange={(option) =>
                setThread({
                  ...thread,
                  topic: {
                    id: option[0].value,
                    label: option[0].label,
                    creator: "",
                    creator_id: 0,
                  },
                })
              }
              options={topics.map((topic) => ({
                value: topic.id,
                label: topic.label,
              }))}
              values={
                thread.topic.id > 0
                  ? [{ value: thread.topic.id, label: thread.topic.label }]
                  : []
              }
            />
            <TextField
              style={{
                marginTop: 20,
              }}
              required
              autoFocus
              margin="dense"
              id="subject"
              name="subject"
              label="Titel"
              type="text"
              fullWidth
              variant="standard"
              value={thread.subject}
              onChange={(e) =>
                setThread({ ...thread, subject: e.target.value })
              }
            />

            <TextEditor
              onChange={(value) =>
                setThread({
                  ...thread,
                  content: value,
                })
              }
              value={thread.content}
              placeholder={"Was liegt dir auf dem Herzen?"}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!createPoll}
                  onChange={(e) => setCreatePoll(e.target.checked ?? false)}
                />
              }
              label="Umfrage erstellen"
            />
            {createPoll && (
              <>
                <TextField
                  margin="dense"
                  label="Umfrage Fragestellung"
                  type="text"
                  fullWidth
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                />
                {pollOptions.map((option, index) => (
                  <TextField
                    key={index}
                    margin="dense"
                    label={`Option ${index + 1}`}
                    type="text"
                    fullWidth
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                  />
                ))}
                <Button onClick={handleAddOption}>Option hinzufügen </Button>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                    />
                  }
                  label="Anonyme Umfrage"
                />
              </>
            )}

            <Box mt={2}>
              <DialogContentText>Dateien hochladen:</DialogContentText>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  setFiles(Array.from(e.target.files));
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleClose}>
                Abbrechen
              </Button>
              <Button variant="contained" endIcon={<SendIcon />} type="submit">
                Senden
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default ThreadForm;
